import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import SectionWidgetContext from '@aurora/shared-client/components/context/SectionWidgetContext';
import WidgetContext from '@aurora/shared-client/components/context/WidgetContext';
import type {
  MessagePagesAndParams,
  BoardPagesAndParams,
  EndUserRouteAndParams
} from '@aurora/shared-client/routes/endUserRoutes';
import type {
  Idea,
  MessageConstraints,
  MessageSorts
} from '@aurora/shared-generated/types/graphql-schema-types';
import {
  ConversationStyle,
  SortDirection
} from '@aurora/shared-generated/types/graphql-schema-types';
import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import { EndUserComponent, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import {
  merge,
  UndefinedValueMergeBehavior
} from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { getQueryParamKeyForWidget } from '../../../helpers/quilt/WidgetHelper';
import { Breakpoint, ItemType, MessageTimestamp, MessageViewVariant } from '../../../types/enums';
import type {
  IdeaMessagesForBoardQuery,
  IdeaMessagesForBoardQueryVariables,
  IdeaMessageViewFragment,
  IdeaStatusInformationQuery,
  IdeaStatusInformationQueryVariables
} from '../../../types/graphql-types';
import PaneledItemList from '../../common/List/PaneledItemList/PaneledItemList';
import type { OverflowSetItem } from '../../common/OverflowSet/OverflowSet';
import EditableWidget from '../../common/Widget/EditableWidget';
import type { WidgetFC } from '../../common/Widget/types';
import type { ItemViewTypeAndProps } from '../../entities/types';
import MessageListTabs, {
  MessageListTabItem
} from '../../messages/MessageListTabs/MessageListTabs';
import MessagesSortOptions from '../../messages/MessagesSortOptions/MessagesSortOptions';
import ideaMessagesQuery from './IdeaMessagesForBoard.query.graphql';
import useEntityViewQuery from '../../useEntityViewQuery';
import useQueryParamValueForWidget from '../../useQueryParamValueForWidget';
import useTranslation from '../../useTranslation';
import type { StatusItem } from '../IdeaMessagesStatusWidgetFilters/IdeaMessagesStatusFilter/IdeaMessagesStatusFilterDropdown';
import IdeaMessagesStatusFilterDropdown from '../IdeaMessagesStatusWidgetFilters/IdeaMessagesStatusFilter/IdeaMessagesStatusFilterDropdown';
import type { IdeaMessagesWidgetProps } from '../IdeaMessagesWidgetEditor/types';
import MessagesWidgetFilters from '../../common/MessagesWidgetFilters/MessagesWidgetFilters';
import useQueryParamArrayForWidget from '../../useQueryParamArrayForWidget';
import localStyles from './IdeaMessagesWidget.module.pcss';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import ideaStatusPropertiesQuery from '../../ideas/IdeaStatusInformation.query.graphql';
import PaneledItemListSubHeader from '../../common/List/PaneledItemListSubHeader/PaneledItemListSubHeader';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import Icons from '@aurora/shared-client/icons';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import Button from '@aurora/shared-client/components/common/Button/Button';
/**
 * NOTE: while making changes to the CSS classes please make sure the cases are handled for different panelTypes
 * border-bottom for Divider and Space styles only appear in mobile view
 * filter icon appears out of the tile in Border style in mobile view
 */

const defaultProps: IdeaMessagesWidgetProps = {
  style: 'list',
  listVariant: {
    type: ListVariant.LIST_GROUP
  },
  pagerVariant: {
    type: PagerVariant.LOAD_MORE
  },
  viewVariant: {
    type: MessageViewVariant.INLINE,
    props: {
      useBoardIcon: false,
      useBody: true,
      useNode: false,
      useTags: true,
      useKudosCount: true,
      useViewCount: true,
      useUnreadCount: true,
      useRepliesCount: true,
      useAvatar: true,
      clampSubjectLines: 2,
      portraitClampBodyLines: 3,
      useAuthorLogin: true,
      useAuthorLoginLink: true,
      useAuthorRank: true,
      useSolvedBadge: false,
      usePreviewMedia: false,
      timeStampType: MessageTimestamp.POST_TIME,
      useMessageTimeLink: true,
      useTextBody: true,
      useVideoPreview: false,
      useVoteComponent: false,
      useMessageStatus: true,
      useReadOnlyIcon: true
    }
  },
  pageSize: 10,
  useTitle: true,
  hideIfEmpty: false,
  panelType: PanelType.DIVIDER,
  lazyLoad: false,
  enablePagination: true,
  useIdeaFilterBar: true,
  filterOrSortToBeApplied: {
    key: MessageListTabItem.TRENDING,
    value: MessageListTabItem.TRENDING,
    groupKey: 'sort'
  },
  detailedListTabs: [
    { key: MessageListTabItem.TRENDING, value: MessageListTabItem.TRENDING, groupKey: 'sort' }
  ]
};

/**
 * final props merged with the props passed to component with default props
 * @param props default props for component
 */
export function getFinalProps(
  props: IdeaMessagesWidgetProps,
  filtersApplied: boolean
): IdeaMessagesWidgetProps {
  const finalProps: IdeaMessagesWidgetProps = merge(defaultProps, props, {
    undefinedMergeBehavior: UndefinedValueMergeBehavior.IGNORE_BEFORE_MERGE,
    mergeNested: false
  });
  if (finalProps.hideIfEmpty) {
    finalProps.hideIfEmpty = finalProps.hideIfEmpty && !filtersApplied;
  }
  return finalProps;
}

const messageSortsMap: Record<string, MessageSorts> = {
  [MessageListTabItem.MOST_REPLIES]: { repliesCount: { direction: SortDirection.Desc } },
  [MessageListTabItem.MOST_RECENT]: {
    conversationLastPostingActivityTime: { direction: SortDirection.Desc }
  },
  [MessageListTabItem.MOST_VIEWED]: { viewsCount: { direction: SortDirection.Desc } },
  [MessageListTabItem.MOST_KUDOED]: { kudosSumWeight: { direction: SortDirection.Desc } },
  [MessageListTabItem.NEWEST]: { postTime: { direction: SortDirection.Desc } },
  [MessageListTabItem.TRENDING]: { popularity: { direction: SortDirection.Desc } }
};

/**
 * Idea messages widget
 *
 * @author Abhijith.Be
 */
const IdeaMessagesWidget: WidgetFC<IdeaMessagesWidgetProps> = props => {
  const { isVisible, ...rest } = props;
  const widgetContext = useContext(WidgetContext);
  const { location } = useContext(SectionWidgetContext);
  const { key: statusParameterKey, value: statusParameterValue } = useQueryParamValueForWidget(
    EndUserQueryParams.SEARCH_FILTER_BY_MESSAGE_STATUS,
    ''
  );

  const selectedStatuses = statusParameterValue ? statusParameterValue.split(',') : [];
  const { key: tagsParameterKey, value: tagsParameterValue } = useQueryParamArrayForWidget(
    EndUserQueryParams.SEARCH_BY_TAGS_FILTER,
    []
  );
  const filtersApplied =
    !!statusParameterValue || (tagsParameterValue && tagsParameterValue.length > 0);
  const finalProps: IdeaMessagesWidgetProps = getFinalProps(rest, filtersApplied);
  const {
    style,
    listVariant,
    pagerVariant,
    viewVariant,
    className,
    pageSize,
    useTitle,
    hideIfEmpty,
    panelType,
    textAlignment,
    enablePagination,
    useIdeaFilterBar,
    filterOrSortToBeApplied,
    detailedListTabs
  } = finalProps;

  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.IDEA_MESSAGES_WIDGET);
  const { formatMessage, loading: textLoading } = i18n;
  const { contextNode } = useContext(AppContext);
  const {
    publicConfig: { messageStatusEnabled }
  } = useContext(TenantContext);
  const { router } = useEndUserRoutes();
  const [showFilterIcon, setShowFilterIcon] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      setIsMobile(ref.current.offsetWidth < Breakpoint.SM);
    });
    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, []);

  const { data: propertiesData, loading: ideaPropertiesLoading } = useQueryWithTracing<
    IdeaStatusInformationQuery,
    IdeaStatusInformationQueryVariables
  >(module, ideaStatusPropertiesQuery, {
    variables: {
      id: contextNode.id,
      useIdeaStatusProperties: true
    },
    skip: !messageStatusEnabled
  });

  /**
   * callback to handle status filter change
   */
  const onStatusFilterChange = useCallback(
    async (items: StatusItem[]) => {
      const statusKeys = items
        .map(item => item.statusKey)
        .filter(key => key !== '')
        .join(',');

      await router.replaceRoute<EndUserRouteAndParams<EndUserPages>>(
        router.getCurrentPageName(),
        router.getPathParams<MessagePagesAndParams>(),
        {
          ...router.getQueryParams(),
          [statusParameterKey]: statusKeys
        },
        { scroll: false }
      );
    },
    [router, statusParameterKey]
  );

  const isIdeaStatusEnabled = messageStatusEnabled
    ? (propertiesData?.coreNode as Idea)?.ideaStatusProperties?.isIdeaStatusEnabled
    : false;

  const tabsMap = {};
  detailedListTabs.forEach(tab => {
    if (tab.groupKey === 'sort') {
      tabsMap[tab.key] = { key: tab.key, group: 'sort' };
    } else if (isIdeaStatusEnabled) {
      tabsMap[tab.value] = {
        key: tab.key,
        group: 'group'
      };
    }
  });

  const tabsList = Object.keys(tabsMap) as MessageListTabItem[];
  const useEmpty = !hideIfEmpty || tabsList.length > 1;

  const [firstTab] = tabsList;
  const { key: queryParamKey, value: queryParamValue } = useQueryParamValueForWidget(
    'tab',
    firstTab
  );

  if (isIdeaStatusEnabled === false && !tabsMap[queryParamValue]) {
    router.replaceRoute<EndUserRouteAndParams<EndUserPages>>(
      router.getCurrentPageName(),
      router.getPathParams<BoardPagesAndParams>(),
      {
        [queryParamKey]: firstTab
      }
    );
  }
  const activeTab = queryParamValue;

  const showSortOptionsForSingleSortTab =
    tabsList.length === 1 && messageSortsMap.hasOwnProperty(activeTab);

  const { key: sortParameterKey, value: sortParameterValue } = useQueryParamValueForWidget(
    EndUserQueryParams.MESSAGE_SORT_OPTION,
    showSortOptionsForSingleSortTab ? activeTab : MessageListTabItem.TRENDING
  );

  if (tabsMap[activeTab]?.group === 'sort' && !showSortOptionsForSingleSortTab) {
    const selectedWidgetId = getQueryParamKeyForWidget(
      widgetContext,
      location,
      EndUserQueryParams.MESSAGE_SORT_OPTION,
      '-'
    );
    const sortParam = router.getUnwrappedQueryParam(selectedWidgetId as EndUserQueryParams, '');
    if (sortParam) {
      router.removeQueryParam([selectedWidgetId as EndUserQueryParams]);
    }
  }

  /**
   * callback to handle status filter change
   */
  const onSortFilterChange = useCallback(
    async (sortKey: string) => {
      await router.replaceRoute<EndUserRouteAndParams<EndUserPages>>(
        router.getCurrentPageName(),
        router.getPathParams<MessagePagesAndParams>(),
        {
          ...router.getQueryParams(),
          [sortParameterKey]: sortKey
        },
        { scroll: false }
      );
    },
    [router, sortParameterKey]
  );

  let sorts: MessageSorts = {};

  if (style !== 'list') {
    sorts =
      filterOrSortToBeApplied.groupKey === 'sort'
        ? messageSortsMap[filterOrSortToBeApplied.key]
        : {};
  } else {
    sorts =
      tabsMap[activeTab]?.group === 'sort' && tabsList.length > 1
        ? messageSortsMap[activeTab]
        : messageSortsMap[sortParameterValue];
  }

  let statusGroupConstraint: MessageConstraints = null;
  if (isIdeaStatusEnabled) {
    if (style !== 'list') {
      statusGroupConstraint =
        filterOrSortToBeApplied.groupKey === 'group'
          ? { statusGroup: { eq: filterOrSortToBeApplied.key } }
          : null;
    } else {
      statusGroupConstraint =
        tabsMap[activeTab].group === 'group'
          ? { statusGroup: { eq: tabsMap[activeTab].key } }
          : null;
    }
  }

  // Multi-select idea status filtering
  // Use statusKey field for both single (eq) and multiple (in) status filtering
  // Backend expects field name "statusKey" regardless of operator
  const statusConstraint =
    selectedStatuses && selectedStatuses.length > 0
      ? selectedStatuses.length === 1
        ? { statusKey: { eq: selectedStatuses[0] } }
        : { statusKey: { in: selectedStatuses } }
      : {};

  const constraints: MessageConstraints = {
    boardId: { eq: contextNode.id },
    depth: { eq: 0 },
    ...(statusGroupConstraint !== null && statusGroupConstraint),
    ...statusConstraint,
    ...(tagsParameterValue &&
      tagsParameterValue.length > 0 && { tagsText: { in: tagsParameterValue } })
  };

  const variables = {
    constraints,
    sorts,
    first: pageSize,
    useKudosCount: true,
    useMessageStatus: true
  };

  const commonProps = {
    ...viewVariant.props,
    ...(viewVariant.props.timeStampType && {
      timeStampType: sorts?.conversationLastPostingActivityTime
        ? MessageTimestamp.LAST_POSTING_ACTIVITY_TIME
        : MessageTimestamp.POST_TIME
    })
  };

  const cardViewVariant = {
    type: viewVariant.type,
    props: {
      ...commonProps,
      textAlignment: textAlignment
    }
  };

  const listViewVariant = {
    type: viewVariant.type,
    props: commonProps
  };

  const queryResult = useEntityViewQuery<
    ItemType.MESSAGE,
    IdeaMessagesForBoardQuery,
    IdeaMessagesForBoardQueryVariables
  >(module, ItemType.MESSAGE, viewVariant, ideaMessagesQuery, {
    variables
  });

  const { data, loading } = queryResult;
  const isRenderFilterIcon = isMobile && style === 'list' && tabsList.length <= 1;
  const isRenderHeader =
    useTitle || (!useTitle && panelType === PanelType.BUBBLE && isRenderFilterIcon);

  useEffect(() => {
    if (!useEmpty && !loading) {
      isVisible(data?.messages.edges.length !== 0);
    }
  }, [data, isVisible, loading, useEmpty]);

  if (textLoading || ideaPropertiesLoading) {
    return null;
  }

  /**
   * Function to render the filter Icon
   *
   * @returns the filter icon that toggles the filter bar
   */
  function renderFilterIcon(): React.ReactElement {
    if (style === 'list' && activeTab !== null && useIdeaFilterBar === true) {
      const filterPadding = !useTitle && tabsList.length <= 1 && panelType !== PanelType.BUBBLE;
      return (
        <div
          className={cx(
            'd-flex',
            { 'lia-align-center': tabsList.length > 1 },
            { 'lia-g-p-15': filterPadding }
          )}
        >
          <Button
            className={cx('lia-g-icon-btn lia-btn lia-filter-btn lia-g-p-0')}
            variant={ButtonVariant.NO_VARIANT}
            onClick={() => setShowFilterIcon(!showFilterIcon)}
            aria-label={formatMessage('ariaLabel.collapseMoreFilters')}
          >
            <Icon
              icon={Icons.FilterIcon}
              size={IconSize.PX_24}
              color={IconColor.GRAY_900}
              testId="IdeaMessagesList.FilterIcon"
              className={cx('lia-filter', { active: showFilterIcon })}
            />
          </Button>
        </div>
      );
    }
  }

  /**
   * Function to render tabbed filters
   */
  function renderTabList(): React.ReactElement {
    if (style === 'list' && tabsList.length > 1) {
      return (
        <PaneledItemListSubHeader
          panel={panelType}
          as={'nav'}
          className={cx({ 'd-flex justify-content-end': isMobile })}
        >
          <MessageListTabs
            activeTab={activeTab as MessageListTabItem}
            items={tabsList}
            queryParamKey={queryParamKey}
            recordHistory={false}
            conversationStyle={ConversationStyle.Idea}
            useBottomBorder={!isMobile ? panelType === PanelType.STANDARD : false}
          />
          {isMobile && renderFilterIcon()}
        </PaneledItemListSubHeader>
      );
    }

    return null;
  }

  const sortOptions: MessageListTabItem[] = [
    MessageListTabItem.TRENDING,
    MessageListTabItem.MOST_RECENT,
    MessageListTabItem.NEWEST,
    MessageListTabItem.MOST_KUDOED,
    MessageListTabItem.MOST_VIEWED,
    MessageListTabItem.MOST_REPLIES
  ];

  const sortFilters = sortOptions.map(sortOption => ({
    key: sortOption,
    value: formatMessage(sortOption)
  }));

  /**
   * Set of Additional filter items
   */
  const ideaFiltersItems: OverflowSetItem[] = [];
  ideaFiltersItems.push({
    key: 'ideaMessagesStatusFilter',
    onRender: (
      <IdeaMessagesStatusFilterDropdown
        contextNode={contextNode}
        selectedStatuses={selectedStatuses}
        className={className}
        onChange={onStatusFilterChange}
        statusGroup={
          tabsMap[activeTab].group !== 'sort'
            ? { groupKey: tabsMap[activeTab].key, groupTitle: activeTab }
            : null
        }
      />
    )
  });

  /**
   * Function to render the filter bar
   */
  function renderFilterBar(): React.ReactElement {
    if (style === 'list' && activeTab !== null && useIdeaFilterBar === true) {
      return (
        <PaneledItemListSubHeader
          panel={panelType}
          as={'nav'}
          className={cx(
            'lia-filter-bar',
            { 'lia-bubble m-0': panelType === PanelType.BUBBLE },
            { 'lia-g-mb-10': !isMobile && panelType === PanelType.BUBBLE },
            { 'border-bottom-0': isMobile && panelType !== PanelType.BUBBLE }
          )}
        >
          <MessagesWidgetFilters
            tagsParameterKey={tagsParameterKey}
            selectedTags={tagsParameterValue}
            nodeId={contextNode.id}
            onChange={() => {}}
            className={cx(
              { 'lia-g-pb-10': panelType !== PanelType.BUBBLE },
              { 'lia-g-py-0': panelType === PanelType.BUBBLE }
            )}
            additionalFilters={ideaFiltersItems}
          />
          {(tabsMap[activeTab].group !== 'sort' || showSortOptionsForSingleSortTab) && (
            <MessagesSortOptions
              selectedSortFilterValue={formatMessage(sortParameterValue)}
              sortFilters={sortFilters}
              onSortFilterChange={onSortFilterChange}
              className={cx('lia-sort-dropdown', {
                'lia-sort-dropdown-bubble': panelType === PanelType.BUBBLE
              })}
            />
          )}
        </PaneledItemListSubHeader>
      );
    }

    return null;
  }

  /**
   * Function to render filters and sorts tabs list
   */
  function renderSubHeaders(): React.ReactElement {
    return (
      <>
        {renderTabList()}
        {isMobile ? (
          <div
            className={cx(
              'd-flex',
              `${showFilterIcon ? 'justify-content-between' : 'justify-content-end'}`,
              { 'lia-bubble p-0 lia-g-mb-10': showFilterIcon && panelType === PanelType.BUBBLE },
              {
                'border-bottom':
                  (showFilterIcon && panelType !== PanelType.BUBBLE) ||
                  (showFilterIcon && tabsList.length <= 1 && panelType === PanelType.DIVIDER) ||
                  (!showFilterIcon &&
                    !useTitle &&
                    tabsList.length <= 1 &&
                    panelType === PanelType.DIVIDER)
              }
            )}
          >
            {showFilterIcon && renderFilterBar()}
            {!useTitle &&
              isRenderFilterIcon &&
              panelType !== PanelType.BUBBLE &&
              renderFilterIcon()}
          </div>
        ) : (
          renderFilterBar()
        )}
      </>
    );
  }

  /**
   * Function to render header
   */
  function renderHeader(): React.ReactElement {
    if (useTitle) {
      return (
        <div className={cx('d-flex justify-content-between')}>
          <h3 className={cx('text-break mb-0 h5')}>{formatMessage('title')}</h3>
          {isRenderFilterIcon && renderFilterIcon()}
        </div>
      );
    } else if (!useTitle && panelType === PanelType.BUBBLE && isRenderFilterIcon) {
      return <div className={cx('d-flex justify-content-end')}>{renderFilterIcon()}</div>;
    }
  }

  /**
   * Function to render Empty State
   */
  function renderEmptyState(): React.ReactElement {
    return (
      <EmptyState alignment={TextAlignment.LEFT} description={formatMessage('emptyDescription')} />
    );
  }

  const useSubHeader = style === 'list' && (useIdeaFilterBar === true || activeTab !== null);

  return (
    <div ref={ref}>
      <EditableWidget<IdeaMessagesWidgetProps> props={finalProps}>
        <PaneledItemList<
          IdeaMessageViewFragment,
          ItemType.MESSAGE,
          ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant>,
          IdeaMessagesForBoardQuery,
          IdeaMessagesForBoardQueryVariables
        >
          type={ItemType.MESSAGE}
          variant={style === 'card' ? cardViewVariant : listViewVariant}
          listVariant={listVariant}
          queryResult={queryResult}
          panel={panelType}
          pagerVariant={pagerVariant}
          pageSize={pageSize}
          headerClassName={cx('text-break mb-0')}
          bodyClassName="lia-g-mb-0"
          useEmpty={useEmpty}
          header={isRenderHeader && renderHeader}
          subHeader={useSubHeader && renderSubHeaders}
          empty={renderEmptyState}
          className={cx(className)}
          footerClassName={cx({ 'lia-g-mt-5 bg-transparent': style === 'card' })}
          useFooter={enablePagination && pagerVariant.type !== PagerVariant.NONE}
          id="IdeaMessagesWidget"
          role="tabpanel"
          showHeadersWhileLoading
        />
      </EditableWidget>
    </div>
  );
};

export default IdeaMessagesWidget;
